import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const PKR = (n) => `Rs ${Number(n || 0).toLocaleString("en-PK")}`;

export default function Menu() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [variants, setVariants] = useState({}); // item_id -> variant name
  const cat = params.get("category") || "all";
  const { add } = useCart();

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    const p = {};
    if (cat !== "all") p.category = cat;
    if (q) p.q = q;
    api.get("/menu", { params: p }).then((r) => setItems(r.data)).catch(() => {});
  }, [cat, q]);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => { (g[i.category] = g[i.category] || []).push(i); });
    return g;
  }, [items]);

  const setCat = (slug) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("category"); else next.set("category", slug);
    setParams(next);
  };

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6" data-testid="menu-page">
      <div className="mb-6">
        <span className="badge-gold">Restaurant</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-white mt-2">Our Menu</h1>
        <p className="text-zinc-400 mt-2">Fresh, hand-crafted and delivered to your door.</p>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="menu-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dishes..."
            className="input-lux pl-9"
          />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-2 px-2">
        <button data-testid="cat-all" onClick={() => setCat("all")} className={`chip ${cat === "all" ? "active" : ""}`}>All</button>
        {cats.map((c) => (
          <button key={c.slug} data-testid={`menu-cat-${c.slug}`} onClick={() => setCat(c.slug)} className={`chip ${cat === c.slug ? "active" : ""}`}>{c.name}</button>
        ))}
      </div>

      {Object.keys(grouped).length === 0 && <div className="text-zinc-500 py-20 text-center">No items found.</div>}

      {Object.entries(grouped).map(([slug, list]) => {
        const name = cats.find((c) => c.slug === slug)?.name || slug;
        return (
          <section key={slug} className="mb-12" data-testid={`menu-section-${slug}`}>
            <h2 className="font-serif text-2xl text-amber-200 mb-4">{name}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((item) => {
                const vName = variants[item.item_id] || item.variants?.[0]?.name || null;
                const v = item.variants?.find((x) => x.name === vName);
                const price = Number(item.price) + Number(v?.price_delta || 0);
                return (
                  <div key={item.item_id} className="card-lux overflow-hidden flex flex-col" data-testid={`food-item-${item.item_id}`}>
                    {item.image_url && <img src={item.image_url} alt={item.name} className="w-full aspect-[5/4] object-cover" loading="lazy" />}
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-white font-medium leading-tight">{item.name}</h3>
                        <div className="text-amber-300 font-mono text-sm whitespace-nowrap">{PKR(price)}</div>
                      </div>
                      {item.description && <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{item.description}</p>}
                      {item.variants?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.variants.map((opt) => (
                            <button
                              key={opt.name}
                              onClick={() => setVariants((s) => ({ ...s, [item.item_id]: opt.name }))}
                              data-testid={`variant-${item.item_id}-${opt.name}`}
                              className={`text-xs px-3 py-1.5 rounded-full border transition ${vName === opt.name ? "bg-amber-500/20 border-amber-500 text-amber-200" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}
                            >
                              {opt.name}{opt.price_delta ? ` +${PKR(opt.price_delta)}` : ""}
                            </button>
                          ))}
                        </div>
                      )}
                      <button
                        data-testid={`add-to-cart-${item.item_id}`}
                        onClick={() => add(item, v)}
                        className="btn-gold mt-auto !py-2.5 mt-4"
                      >
                        <Plus size={16} /> Add to cart
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
